import { createContext, useCallback, useContext, useMemo } from 'react';
import { useHistory } from 'react-router';
import qs from 'query-string';
import { reject } from 'ramda';

import { TransactionPaymentMethod, TransactionStatus } from '__generated__/GQL';
import { FCWithChildren } from 'common/types';

import { getFiltersLength } from '../Transactions/utils';

const ARRAY_FORMAT = 'comma';

export type TransactionsFilters = {
  afterDate?: string;
  beforeDate?: string;
  companyUserId?: string;
  payin: boolean;
  paymentMethod: TransactionPaymentMethod[];
  payout: boolean;
  q: string;
  status: TransactionStatus[];
  valueGreaterThan: number | null;
  valueLesserThan: number | null;
  withoutReceipts: boolean;
};

export const defaults: TransactionsFilters = {
  afterDate: '',
  beforeDate: '',
  companyUserId: undefined,
  payin: false,
  paymentMethod: [],
  payout: false,
  q: '',
  status: [],
  valueGreaterThan: null,
  valueLesserThan: null,
  withoutReceipts: false,
};

interface ContextType {
  filters: TransactionsFilters;
  filtersCount: number;
  updateFilters: (filters: Partial<TransactionsFilters>) => void;
}

export const TransactionsFiltersContext = createContext<ContextType>({
  filters: defaults,
  filtersCount: 0,
  updateFilters: () => {},
});

type UrlFilterValue =
  | (string | boolean | number)[]
  | string
  | boolean
  | number
  | null;

const transformUrlValueToEnumArray = <
  T extends TransactionStatus | TransactionPaymentMethod,
>(
  value: UrlFilterValue,
  possibleValues: T[],
): T[] => {
  if (value === null) {
    return [];
  }

  // Since value is from the URL and can be parsed by query-string as a simple string or an array
  const arrayValue = Array.isArray(value) ? value : [value];

  // Filter values that are not included on the enum
  return possibleValues.filter((possibleValue) =>
    arrayValue.includes(possibleValue),
  );
};

const searchToFilters = (
  search: string,
): {
  filters: TransactionsFilters;
  otherSearchParams: Record<string, unknown>;
} => {
  const queryParams = qs.parse(search, {
    arrayFormat: ARRAY_FORMAT,
    parseBooleans: true,
  });

  const { filters, otherSearchParams } = Object.keys(queryParams).reduce(
    (acc, key) => {
      if (Object.keys(defaults).includes(key)) {
        return {
          ...acc,
          filters: {
            ...acc.filters,
            [key]: queryParams[key],
          },
        };
      }
      return {
        ...acc,
        otherSearchParams: {
          ...acc.otherSearchParams,
          [key]: queryParams[key],
        },
      };
    },
    {
      filters: {} as TransactionsFilters,
      otherSearchParams: {} as Record<string, unknown>,
    },
  );

  const allowedPaymentMethods = Object.values(TransactionPaymentMethod);

  return {
    filters: {
      ...filters,
      payin: Boolean(filters.payin),
      paymentMethod: transformUrlValueToEnumArray(
        filters.paymentMethod,
        allowedPaymentMethods,
      ),
      payout: Boolean(filters.payout),
      q: filters.q?.toString() ?? '',
      status: transformUrlValueToEnumArray(
        filters.status,
        Object.values(TransactionStatus),
      ),
      valueGreaterThan: Number(filters.valueGreaterThan) || null,
      valueLesserThan: Number(filters.valueLesserThan) || null,
      withoutReceipts: Boolean(filters.withoutReceipts),
    },
    otherSearchParams,
  };
};

// Curry function that removes all properties that are equals to `false`
const omitFalsyValue = reject((filter) => !filter);

export const TransactionsFiltersProvider: FCWithChildren = ({ children }) => {
  const history = useHistory();

  const { filters, otherSearchParams } = useMemo(
    () => searchToFilters(history.location.search),
    [history.location.search],
  );

  const updateFilters = useCallback(
    (newFilters: Partial<TransactionsFilters>) => {
      history.push({
        search: qs.stringify(
          {
            ...omitFalsyValue({ ...filters, ...newFilters }),
            ...otherSearchParams,
          },
          {
            arrayFormat: ARRAY_FORMAT,
            skipEmptyString: true,
            skipNull: true,
          },
        ),
      });
    },
    [history, filters, otherSearchParams],
  );

  const filtersCount = useMemo(
    () => getFiltersLength(filters, ['q', 'companyUserId']),
    [filters],
  );

  return (
    <TransactionsFiltersContext.Provider
      value={{
        filters,
        filtersCount,
        updateFilters,
      }}
    >
      {children}
    </TransactionsFiltersContext.Provider>
  );
};

export function useTransactionsFilters() {
  const context = useContext(TransactionsFiltersContext);

  if (!context) {
    throw new Error(
      'useTransactionsFilters can only be used inside of a TransactionsFiltersProvider',
    );
  }

  return context;
}
